/**
 * Shared GPO Affiliation visibility rules for the Speaker Profile, Program
 * Request, and Program Details pages, so the business rule is implemented
 * once instead of being duplicated per page.
 *
 * `isGpoAffiliationVisible()` (used by Program Request and Program Details,
 * which each have a single project/program type context) is visible only
 * when all of the following are true:
 *  - `sb.meeting_request.enable_gpo_affiliation` is enabled on the current
 *    Project Program Type configuration (the same configuration object/path
 *    already used for `enable_national_recruitment`). Callers resolve this
 *    value themselves using whichever existing mechanism their page already
 *    uses for that configuration, and pass the resolved value in.
 *  - the current brand_code is present in the `GPO_AFFILIATION_BRAND_CODES`
 *    config list (same `ref_config`/`Service\Configuration` mechanism as
 *    `FAQ_BRAND_CODES`).
 *  - the current opco_code is present in the `GPO_AFFILIATION_OPCOS` config
 *    list (same mechanism as `PHARMA_OPCOS` / `AESTHETICS_OPCOS`).
 *
 * `isGpoAffiliationVisibleByBrandAndOpco()` (used by Speaker Profile, which
 * has no single project/program type context) checks only the brand_code and
 * opco_code conditions above, ignoring the Project Program Type configuration.
 *
 * @param {boolean} enableGpoAffiliation sb.meeting_request.enable_gpo_affiliation,
 *   already resolved by the caller from the Project Program Type configuration.
 * @param {string|string[]|null|undefined} brandCodes current brand_code(s) to check
 *   against GPO_AFFILIATION_BRAND_CODES.
 * @returns {boolean}
 */
export function isGpoAffiliationVisible(enableGpoAffiliation, brandCodes) {
    if (enableGpoAffiliation != true) {
        return false;
    }

    return isConfiguredGpoBrandCode(brandCodes) && isConfiguredGpoOpco();
}

/**
 * Speaker Profile-specific GPO Affiliation visibility rule.
 *
 * The Speaker Profile page has no single project/program type context, so
 * unlike `isGpoAffiliationVisible()`, this does not check the Project Program
 * Type's `sb.meeting_request.enable_gpo_affiliation` configuration. Visibility
 * depends only on the configured brand code(s) and opco code.
 *
 * @param {string|string[]|null|undefined} brandCodes current brand_code(s) to check
 *   against GPO_AFFILIATION_BRAND_CODES.
 * @returns {boolean}
 */
export function isGpoAffiliationVisibleByBrandAndOpco(brandCodes) {
    return isConfiguredGpoBrandCode(brandCodes) && isConfiguredGpoOpco();
}

function getConfiguredList(configKey) {
    const raw = app.appData.configs?.[configKey];

    if (Array.isArray(raw)) {
        return raw;
    }

    try {
        const parsed = JSON.parse(raw || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

function isConfiguredGpoBrandCode(brandCodes) {
    const configuredBrandCodes = getConfiguredList('GPO_AFFILIATION_BRAND_CODES');
    const codes = (Array.isArray(brandCodes) ? brandCodes : [brandCodes]).filter(Boolean);

    return codes.some((brandCode) => configuredBrandCodes.includes(brandCode));
}

function isConfiguredGpoOpco() {
    const configuredOpcos = getConfiguredList('GPO_AFFILIATION_OPCOS');

    return configuredOpcos.includes(app.appData.curPosition?.opco_code);
}
